import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { portalApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, CalendarDays } from "lucide-react";

export function PagoExito() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [estado, setEstado] = useState(null);
  const [cuenta, setCuenta] = useState(6);

  useEffect(() => {
    if (!sessionId) return;
    let intentos = 0;
    const tick = async () => {
      try {
        const { data } = await portalApi.get(`/payments/status/${sessionId}`);
        setEstado(data);
        if (data.payment_status !== "paid" && intentos < 10) {
          intentos += 1;
          setTimeout(tick, 2000);
        }
      } catch {
        setEstado({ payment_status: "error" });
      }
    };
    tick();
  }, [sessionId]);

  const pagado = estado?.payment_status === "paid";

  // al confirmarse el pago llevamos a la familia a su calendario de clases
  useEffect(() => {
    if (!pagado) return;
    const id = setInterval(() => setCuenta((c) => c - 1), 1000);
    const salto = setTimeout(() => navigate("/portal?tab=calendario"), 6000);
    return () => { clearInterval(id); clearTimeout(salto); };
  }, [pagado, navigate]);

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <Card className="rounded-2xl p-10 max-w-md w-full text-center fade-up" data-testid="pago-exito-page">
        {!estado && <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />}
        {estado && (pagado
          ? <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
          : <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />)}
        <h1 className="font-display text-3xl font-extrabold mt-6" data-testid="pago-estado-titulo">
          {pagado ? "¡Pago procesado correctamente!" : "Confirmando el pago…"}
        </h1>
        {pagado ? (
          <div className="mt-4 space-y-2" data-testid="pago-confirmacion-detalle">
            <p className="text-sm font-semibold">
              {estado.plan_nombre || "Tu plan"} · {estado.amount} €
            </p>
            <p className="text-sm text-primary font-semibold">Tu suscripción ya está activa</p>
            <p className="text-sm text-muted-foreground">
              Hemos agendado sus clases y tienes la factura desglosada en tu área de familias.
            </p>
            <p className="text-xs text-muted-foreground pt-2" data-testid="pago-redireccion-aviso">
              Te llevamos al calendario en {Math.max(cuenta, 0)} s…
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-3">
            Estamos verificando el pago con Stripe. No cierres esta ventana.
          </p>
        )}
        <Button asChild className="rounded-full font-semibold mt-8 w-full" data-testid="volver-portal-btn">
          <Link to="/portal?tab=calendario">
            <CalendarDays className="h-4 w-4 mr-2" /> Ver mi calendario de clases
          </Link>
        </Button>
      </Card>
    </div>
  );
}

export function PagoCancelado() {
  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <Card className="rounded-2xl p-10 max-w-md w-full text-center fade-up" data-testid="pago-cancelado-page">
        <XCircle className="h-12 w-12 text-muted-foreground mx-auto" />
        <h1 className="font-display text-3xl font-extrabold mt-6">Pago cancelado</h1>
        <p className="text-sm text-muted-foreground mt-3">No se ha realizado ningún cargo.</p>
        <Button asChild className="rounded-full font-semibold mt-8 w-full">
          <Link to="/portal">Volver a mi área</Link>
        </Button>
      </Card>
    </div>
  );
}
